/**
 * ActiveModuleChip — "Active module", the heading of whatever sits below Apps.
 *
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  ⛔ EXTRACTED, NOT REWRITTEN. THE MARKUP AND THE CLASSES ARE UNCHANGED.       ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 * This block lived inline in `Sidebar.tsx` and renders on CRM, Dashboard, Work,
 * Communication and every other dock module. `/mail` is an own-rail route, so the
 * app's `Sidebar` does not render there at all (`ownRailRoutes.ts`) and Mail had
 * no chip — it went straight from Apps to a bare "Mail" heading, which was the one
 * place in the app where the two-group rail lost its middle.
 *
 * ⚠️ **THE FIX IS A SECOND CALLER, NOT A SECOND IMPLEMENTATION.** The same
 * reasoning as `AppsRail`, which has exactly these two hosts for exactly this
 * reason: a copy of five lines of JSX in `MailSidebar` would drift from this one
 * the first time the chip's shape changed, and nothing would report it. This
 * repo's architecture law — never build twice.
 *
 * The classes (`pq-ctx-mod`, `pq-modchip`, `pq-ctx-eyebrow`, `pq-ctx-name`) and
 * the `color-mix` tint are carried over verbatim from `Sidebar.tsx`, so every
 * screen that already had this renders exactly as before.
 *
 * ── ⚠️ ONE THING THE CALLER SUPPLIES THAT THE DOCK USED TO ────────────────────
 * `Sidebar` reads `icon`, `color` and the label off the active `DOCK` item. Mail
 * has no dock item — `dockKeyForPath('/mail')` returns `null`, which is precisely
 * why the chip was missing — so `MailSidebar` passes them explicitly.
 */

import React from 'react';

export interface ActiveModuleChipProps {
  /** The small monochrome glyph. It inherits `color` from the tile around it. */
  icon: React.ReactNode;
  /** The tile's tint. Rendered at 22% behind the glyph, as the dock items are. */
  color: string;
  /** What the module or app is called, in the reader's language. */
  name: string;
}

export default function ActiveModuleChip({ icon, color, name }: ActiveModuleChipProps) {
  return (
    <div className="pq-ctx-mod" data-testid="active-module-chip" data-module-name={name}>
      <span
        className="pq-modchip"
        style={{
          background: `color-mix(in srgb, ${color} 22%, transparent)`,
          color,
        }}
      >
        {icon}
      </span>
      <div>
        <div className="pq-ctx-eyebrow">Active module</div>
        <div className="pq-ctx-name">{name}</div>
      </div>
    </div>
  );
}
